import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {
  BackgroundLeftIcon,
  BackgroundSpreadIcon,
  BackgroundRightIcon,
  Button,
} from '@taopix/taopix-design-system';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {Common, PageAPI, PageDetectionActionType, PageStyleType} from '@taopix/taopix-core';
import {ShuffleLayoutArgs} from '@taopix/taopix-core/dist/types/types';
import {ShuffleLayoutButtonState} from './AutoLayoutButton';

type AutoLayoutOptionButtonProps = {
  buttonState: ShuffleLayoutButtonState;
  api: PageAPI;
  onRequestLayout: (payload: ShuffleLayoutArgs, detectionArea: PageDetectionActionType) => void;
};

const AutoLayoutOptionButtons = ({
  buttonState,
  api,
  onRequestLayout,
}: AutoLayoutOptionButtonProps) => {
  const ui = useUiState();
  const {t} = useTranslation();

  const buttonSizingClasses = classNames(
    'px-xs',
    'shrink-0',
    ...(ui.state.isSmallScreen
      ? [
          // 'w-[calc(100vw/3.5)]',
          'min300:w-[calc(100vw/3.5)]',
          'min400:w-[calc(100vw/4.5)]',
          'min500:w-[calc(100vw/5.5)]',
          'min600:w-[calc(100vw/6.5)]',
          'min700:w-[calc(100vw/7.5)]',
          'min800:w-[calc(100vw/8.5)]',
          'min900:w-[calc(100vw/9.5)]',
          'min1000:w-[calc(100vw/10.5)]',
        ]
      : [''])
  );

  const optionButtonProps: Partial<ButtonProps> = {
    stacked: true,
    buttonStyle: 'standard',
    boxless: ui.state.isSmallScreen,
    size: 'small',
    corners: ui.state.isSmallScreen ? 'theme' : 'square',
    className: ui.state.isSmallScreen ? buttonSizingClasses : undefined,
  };

  const onButtonClick = useCallback(
    (type: PageDetectionActionType) => {
      if (!api) {
        return void 0;
      }

      const getTypeString = (action: PageDetectionActionType): 'left' | 'right' | 'spread' => {
        switch (action) {
          case PageDetectionActionType.CurrentLeft: {
            return 'left';
          }
          case PageDetectionActionType.CurrentSpread: {
            return 'spread';
          }
          case PageDetectionActionType.CurrentRight: {
            return 'right';
          }
          default:
            return 'spread';
        }
      };

      const applyTo = getTypeString(type);
      const pageStyleType = Common.isPageCover(api.getPageNumber())
        ? PageStyleType.Spread
        : ['left', 'right'].includes(applyTo)
        ? PageStyleType.Single
        : PageStyleType.Spread;

      const payload = {
        pageNumber: api.getPageNumber(),
        pageType: pageStyleType,
        type: applyTo,
      };

      onRequestLayout(payload, type);
    },
    [api]
  );

  return (
    <>
      {buttonState.left && (
        <Button
          label={t('str_LabelShuffleLeftSide')}
          startIcon={<BackgroundLeftIcon />}
          {...optionButtonProps}
          onClick={() => onButtonClick(PageDetectionActionType.CurrentLeft)}
        />
      )}
      {buttonState.spread && (
        <Button
          label={t('str_LabelShuffleWholeLayout')}
          startIcon={<BackgroundSpreadIcon />}
          {...optionButtonProps}
          onClick={() => onButtonClick(PageDetectionActionType.CurrentSpread)}
        />
      )}
      {buttonState.right && (
        <Button
          label={t('str_LabelShuffleRightSide')}
          startIcon={<BackgroundRightIcon />}
          {...optionButtonProps}
          onClick={() => onButtonClick(PageDetectionActionType.CurrentRight)}
        />
      )}
    </>
  );
};

export default AutoLayoutOptionButtons;
